import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { axiosInstance } from '../axiosInstance';
import { BiLoader } from 'react-icons/bi';
import { CheckCircle, ErrorOutline } from '@mui/icons-material';

export default function InvoicePaymentPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [invoice, setInvoice] = useState(null);

  const { invoiceId } = useParams();

  useEffect(() => {
    // Immediately initialize payment
    initializePayment();
    // eslint-disable-next-line
  }, [invoiceId]);

  const initializePayment = async () => {
    try {
      setLoading(true);

      // First, fetch the invoice details for potential status checks
      const responseInvoice = await axiosInstance.post(`/api/invoice/${invoiceId}/payment/initialize`);
      const dataInvoice = responseInvoice.data;

      if (dataInvoice.error || !dataInvoice.data) {
        setError(dataInvoice.message || 'Invoice not found');
        setInvoice(null);
        setLoading(false);
        return;
      }

      setInvoice(dataInvoice.data);

      if (dataInvoice.data.status === 'completed') {
        // If already paid, show the paid state
        setLoading(false);
        return;
      }

      const paymentInitData = dataInvoice;

      if (paymentInitData.error) {
        setError(paymentInitData.message || 'Failed to initialize payment');
        setLoading(false);
        return;
      }

      // Immediately route to the Paystack authorization_url from the response
      if (
        paymentInitData.data &&
        paymentInitData.data.authorization_url
      ) {
        window.location.href = paymentInitData.data.authorization_url;
      } else {
        setError('Authorization URL not found in payment response.');
        setLoading(false);
      }
    } catch (err) {
      setError('Failed to initialize payment');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <BiLoader className="w-8 h-8 text-green-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-md w-full text-center">
          <ErrorOutline className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // If invoice is already completed (paid)
  if (invoice?.status === 'completed') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Already Paid</h2>
          <p className="text-gray-600 mb-6">This invoice has already been paid.</p>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">
              Invoice #{invoice.invoice_no}
            </p>
            <p className="text-2xl font-bold text-gray-900 mt-2">
              ₦{Number(invoice.total).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // No UI to render, since we're redirecting to Paystack in-process
  return null;
}